import * as angular from 'angular';
import * as _ from 'lodash';

import {
    Application,
    ApplicationUserViewModel,
    ComponentDefinition,
    Group,
    IApprovalStrategy,
    Pattern,
    WorkflowDTO
}
    from '../../models';
import { IWorkflowService, IStoreService, IapplicationUserService, ICurrentApplicationService } from '../../services';
import { StoreElement, Store } from '../../stateManagement';
import { DisplayInfo } from './DisplayInfo';
import { IWorkflowOverviewStateService } from './workflowOverviewState.service';
import { IRootScope } from '../../IRootScope';
import { guidEmpty, languageChanged } from '../../common';

export type AugmentedWorkflowDTO = WorkflowDTO & { displayInfo: DisplayInfo };

class WorlfowsOverviewStateController implements angular.IComponentController, angular.IOnDestroy {
    application: Application;
    approvalStrategies: _.Dictionary<IApprovalStrategy>;
    groups: _.Dictionary<Group>;
    isLoading: boolean;
    users: _.Dictionary<ApplicationUserViewModel>;
    workflows: AugmentedWorkflowDTO[];
    searchCriteria: Pattern<AugmentedWorkflowDTO>[];

    private _appGuid: string;
    private _garbage: () => void;
    private _unsubscribe: () => void;
    private _storeInit: angular.IDeferred<void>;

    static $inject = [
        '$rootScope',
        '$q',
        'storeService',
        'workflowService',
        'currentApplicationService',
        'applicationUserService',
        'workflowOverviewStateService'
    ];

    constructor(
        private _$rootScope: IRootScope,
        private _$q: angular.IQService,
        private _storeService: IStoreService,
        private _workflowService: IWorkflowService,
        currentApplicationService: ICurrentApplicationService,
        private _applicationUserService: IapplicationUserService,
        private _workflowOverviewStateService: IWorkflowOverviewStateService
    ) {
        this._appGuid = currentApplicationService.getApplicationConfiguration().guid;
        this._storeInit = this._$q.defer<void>();
    }

    $onInit() {
        this.searchCriteria = [
            {
                label: 'Trigger name',
                placeholder: 'Search pattern on trigger name',
                property: 'tn',
                model: 'tn',
                isMatch: (dto, regex) => regex.test(dto.displayInfo.content)
            },
            {
                label: 'Action name',
                placeholder: 'Search pattern on action name',
                property: 'an',
                model: 'an',
                isMatch: (dto, regex) => {
                    return dto.actions.filter(a => {
                        let isMatch = regex.test(a.subject);
                        if (!isMatch && a.groupGuid && a.groupGuid !== guidEmpty && this.groups[a.groupGuid]) {
                            isMatch = regex.test(this.groups[a.groupGuid].name);
                        }
                        return isMatch;
                    }).length > 0;
                }
            },
            {
                label: 'Trigger status',
                placeholder: 'Search pattern on trigger status',
                property: 'ts',
                model: 'ts',
                isMatch: (dto, regex, originalString) => this.statusMatch(regex, dto.trigger)
            },
            {
                label: 'Action status',
                placeholder: 'Search pattern on action status',
                property: 'as',
                model: 'as',
                isMatch: (dto, regex, originalString) => {
                    return dto.actions
                        .filter(a => this.statusMatch(regex, a))
                        .length > 0;
                }
            }
        ];
        this.isLoading = true;
        this._unsubscribe = this._storeService.register(
            store => this.refreshStore(store),
            [StoreElement.Application, StoreElement.Groups, StoreElement.ApprovalStrategies]
        );
        this._garbage = this._$rootScope.$on(languageChanged, () => {
            if (this.workflows) {
                this.workflows = this.workflows.map(w => {
                    return {
                        trigger: w.trigger,
                        actions: w.actions,
                        displayInfo: this._workflowOverviewStateService.getTriggerDisplayInfo(w.trigger, this.users, this.approvalStrategies)
                    };
                });
            }
        });

        this.refresh();
    }

    private statusMatch(regex: RegExp, item: { isDisabled: boolean }) {
        if (regex.test('active')) {
            return !item.isDisabled;
        } else if (regex.test('disabled')) {
            return !!item.isDisabled;
        } else {
            return false;
        }
    }


    refreshStore(store: Store): void {
        this.groups = store.groups.dictionary;
        this.approvalStrategies = store.approvalStrategies.dictionary;
        this.application = store.application.value;
        this._storeInit.resolve();
    }

    $onDestroy(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
        if (this._garbage) {
            this._garbage();
        }
    }

    private refresh() {
        this.isLoading = true;

        this._storeInit.promise
            // TODO : will be shipped by store sometime
            .then(() => this._applicationUserService.getAllUsersWithRoles(this._appGuid))
            .then(users => this.setUsers(users))
            .then(() => this._workflowService.getOverview())
            .then(workflows => {
                this.workflows = workflows.map(w => {
                    return {
                        trigger: w.trigger,
                        actions: w.actions,
                        displayInfo: this._workflowOverviewStateService.getTriggerDisplayInfo(w.trigger, this.users, this.approvalStrategies)
                    };
                });
            })
            .finally(() => this.isLoading = false);
    }

    private setUsers(vms: ApplicationUserViewModel[]): _.Dictionary<ApplicationUserViewModel> {
        const users = {};
        vms.forEach(vm => users[vm.name] = vm);
        this.users = users;
        return users;
    }
}

export const wtWorkflowsOverviewState: ComponentDefinition = {
    componentId: 'wtWorkflowsOverviewState',
    controller: WorlfowsOverviewStateController,
    template: require('./workflowsOverviewState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};